import React from 'react';
import {
  Stack,
  Text,
  Heading,
  UnorderedList,
  ListItem,
  Link,
} from '@chakra-ui/react';
import Layout from '../../components/Layout';
import ContentWithStickySidebar from '../../components/shared/ContentWithStickySidebar';
import HeroWithBackgroundImage from '../../components/shared/HeroWithBackgroundImage';
import DefinitionsSection from './DefinitionsSection';
import { SUPPORT_EMAIL, ADDRESS_LINE_1, ADDRESS_LINE_2 } from '../../constants';
import HeroImage from '../../images/TrustLPHeader.webp';

const TermsOfUseHeader = ({ children }: { children: string }) => (
  <Heading py="5">{children}</Heading>
);

const SupportLink = () => (
  <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
);

const TermsOfUse = () => (
  <Layout title="Terms">
    <HeroWithBackgroundImage
      imageUrl={HeroImage}
      backgroundColor="'rgba(255,255,255,.55)'"
      title="HOVER Terms of Use"
    />
    <ContentWithStickySidebar>
      <Stack spacing="3">
        <Text>
          By downloading and/or using the Software and Services (as defined
          below), you accept and agree to comply with these Terms of Use
          (alternatively referred to as the “Standard Terms and Conditions”),
          which will govern your use of the Software and Services.
        </Text>
        <Text>
          THESE TERMS OF USE CONSTITUTE A BINDING LEGAL AGREEMENT BETWEEN YOU
          AND HOVER. IF YOU DO NOT WISH TO BE BOUND BY THESE TERMS OF USE, DO
          NOT DOWNLOAD THE HOVER APP OR DO NOT USE THE SOFTWARE AND SERVICES.
        </Text>{' '}
        <Text>
          These Terms of Use, with any applicable Order Documentation and
          exhibits thereto, or any Pricing Amendment that incorporates these
          Terms of Use, shall be the “Agreement” between you and HOVER
          (collectively, the “Parties”). If you are a Sponsor (as defined below)
          who has executed an agreement with HOVER which is still in force, that
          agreement and not these Terms of Use shall apply to you.
        </Text>
        <TermsOfUseHeader>1. Certain Definitions</TermsOfUseHeader>
        <DefinitionsSection />
        <TermsOfUseHeader>2. Requirements of the HOVER App</TermsOfUseHeader>
        <Text>
          The HOVER App is only compatible with those smartphones and operating
          systems identified and certified by HOVER from time to time. The use
          of the HOVER App requires and utilizes internet connectivity and data.
          Carrier and provider rates for data may apply and you are responsible
          for any such charges. Use of the HOVER App could result in significant
          data usage.
        </Text>
        <Text>
          The functional use of the HOVER App and the production of HOVER Models
          and other HOVER Deliverables may be dependent on the data related to
          geographic location and geo-positional data, and you acknowledge and
          agree that any failure to provide (or make accessible) that data may
          limit the functionality of the HOVER App and the generation of HOVER
          Models and other HOVER Deliverables.
        </Text>
        <Text>
          The Software and Services are dependent on the accuracy of the images
          submitted, in the format, number, location, clarity and sequence
          required by or requested through the HOVER App and the other data and
          information required by or requested through the HOVER App. User, or
          applicable Sponsor, acknowledges and agrees that failure to properly
          provide such images, data and information may limit or prevent the
          functionality of the HOVER App and the ability of HOVER to deliver
          HOVER Models or other HOVER Deliverables.
        </Text>
        <Text>
          HOVER has no liability for errors, unreliable operation, or other
          issues resulting from use of the HOVER App or HOVER Deliverables on or
          in connection with rooted or jail broken devices or use on any mobile
          device that is not in conformance with the manufacturer’s original
          specifications, including use of modified versions of the operating
          system (collectively, “Modified Devices”). Use of the HOVER App or
          HOVER Deliverables on Modified Devices will be at the User’s, or
          applicable Sponsor’s, sole and exclusive risk and liability.
        </Text>
        <TermsOfUseHeader>
          3. Consent to Information Collection and Use
        </TermsOfUseHeader>
        <Text>
          <Text as="span">
            You hereby give HOVER consent to collect data related to you through
            the HOVER App or applicable Co-branded App. Collected data may
            include personal information, Image Data, or Property Information.
            HOVER will manage collected data in accordance with Data Privacy
            Laws. A full description of the information collected and purposes
            of the collection is available at hover.to/privacy. Users acting on
            behalf of the resident of a property, such as either as a Sponsored
            User or Service Provider, must obtain consent of the resident of the
            applicable Image Data and Property Information prior to collection
            and submission through the HOVER App.
          </Text>
        </Text>
        <Text>
          <Text as="span">
            For interactions between a User and a Sponsor through HOVER, these
            Terms of Use shall still be applicable and constitute a legal
            agreement between the User, HOVER and the applicable Sponsor. For
            example, if you receive a request to use the HOVER App from a
            Sponsor you have a relationship with, the collection of information
            through the HOVER App as part of that transaction will be subject to
            these Terms of Use.
          </Text>
        </Text>
        <Text>
          <Text as="span">
            The applicable Sponsor and its subsidiaries are third-party
            beneficiaries of these Terms of Use, and have the right (and shall
            be deemed to have accepted the right) to enforce these Terms of Use.
            Additionally, any information provided by a User, as well as any
            HOVER Deliverables ordered by or provided to a User, may be made
            available to HOVER as well as the applicable Sponsor, its affiliates
            and its Service Providers for use in accordance with HOVER’s and the
            applicable Sponsor’s published privacy policies such as:
          </Text>
        </Text>
        <UnorderedList>
          <ListItem ml="7">
            <Text as="span">
              processing and managing your purchase and use of our products and
              services;
            </Text>
          </ListItem>
          <ListItem ml="7">
            <Text as="span">
              verifying your identity and responding to your inquiries or
              requests;
            </Text>
          </ListItem>
          <ListItem ml="7">
            <Text as="span">
              creating and delivering personalized promotions;
            </Text>
          </ListItem>
          <ListItem ml="7">
            <Text as="span">
              communicating with you by mail, telephone, facsimile, e-mail,
              mobile alerts, SMS text messaging or other reasonable means about:
            </Text>
            <UnorderedList>
              <ListItem ml="7">
                <Text as="span">
                  HOVER or the applicable Sponsor and its affiliates;
                </Text>
              </ListItem>
              <ListItem ml="7">
                <Text as="span">
                  products of HOVER or the applicable Sponsor that may be
                  applicable to you;
                </Text>
              </ListItem>
              <ListItem ml="7">
                <Text as="span">
                  managing permissions for access to HOVER Deliverables already
                  provided to you; and
                </Text>
              </ListItem>
            </UnorderedList>
          </ListItem>
          <ListItem ml="7">
            <Text as="span">marketing efforts; and</Text>
          </ListItem>
          <ListItem ml="7">
            <Text as="span">
              furthering HOVER or the applicable Sponsor and its affiliates
              business and support purposes, such as to perform data analysis,
              audits, fraud monitoring and prevention, to enhance, improve or
              modify the services, to identify usage trends, determine the
              effectiveness of promotional campaigns and to operate and expand
              business activities.
            </Text>
          </ListItem>
        </UnorderedList>
        <Text>
          <Text as="span">
            IF AT ANY TIME YOU DESIRE FOR HOVER NOT TO SHARE YOUR INFORMATION
            WITH ANY SPONSOR, SPONSOR AFFILIATE, SERVICE PROVIDER OR OTHERWISE,
            YOU CAN OPT-OUT BY EMAILING HOVER AT <SupportLink />.
          </Text>
        </Text>
        <Text>
          <Text as="span">
            IF YOU ARE A SERVICE PROVIDER WHO HAS DOWNLOADED AND/OR IS USING A
            SPONSORED APP, YOU ALSO HEREBY ACKNOWLEDGE AND CONSENT TO THE
            SPONSOR’S OR CUSTOMER’S ABILITY TO ACCESS, DISCLOSE, RESTRICT,
            MONITOR, COMPILE INFORMATION AND GENERATE REPORTS FROM YOUR USE OF
            THE SPONSORED APP AND THE SOFTWARE AND SERVICES.
          </Text>
        </Text>
        <TermsOfUseHeader>4. Grant of License; Terms of Use</TermsOfUseHeader>
        <Text>
          <b>(a) Software and Services.</b>
          <Text as="span">
            {' '}
            HOVER grants to User, or applicable Sponsor, a non-exclusive,
            revocable, non-transferable license to those rights necessary to use
            the Software and Services as accessible to User or Sponsor in
            accordance with the Order Documentation, if any. The foregoing shall
            not be construed as entitling User or Sponsor to physical delivery
            or possession of any software comprising the Software and Services.
            The Software and Services are licensed, not sold, to User or
            Sponsor. HOVER retains all title in and to the Software and
            Services. Other than the limited license granted, User or Sponsor
            has no other right, title or interest in the Software and Services.
            User or Sponsor shall not: (i) decompile, reverse engineer,
            disassemble, attempt to derive the source code of, or decrypt all or
            any portion of the Software and Services; (ii) build a competitive
            product or service using information obtained from the Software and
            Services; (iii) make any modification, adaptation, improvement,
            enhancement, translation or derivative work from the Software and
            Services; (iv) remove, alter or obscure any proprietary notice
            (including any notice of copyright or trademark) of HOVER or its
            affiliates, partners, suppliers or the licensors of the Software and
            Services, or otherwise obscure or modify the manner in which the
            Software and Services are displayed; (v) use any robot, spider or
            other automatic program or device to monitor, copy, summarize, or
            otherwise extract information from the HOVER Models or other HOVER
            Deliverables, in whole or in part; or (vi) license, sell, rent,
            lease, transfer, assign, distribute, display, host, outsource,
            disclose, permit timesharing or service bureau use, or otherwise
            commercially exploit or make the Software and Services, programs or
            materials available, to any third party other than, as expressly
            permitted under the terms of this Agreement.
          </Text>
        </Text>
        <Text>
          <b>(b) HOVER Report and Derived Data.</b>
          <Text as="span">
            {' '}
            For the avoidance of doubt, for those Sponsors acting on behalf of a
            resident or User, the Sponsor shall be entitled to save and use a
            copy of the HOVER Report together with the related Derived Data and
            otherwise make use of it with respect to a Subject Property provided
            it shall not (i) directly or indirectly sell or make any of the
            Derived Data available to third parties on an aggregated basis (ii)
            use, or enable or permit a third-party to use, any of the Derived
            Data to build a product or service that directly competes with the
            business of HOVER as comprised as of the date this Agreement is
            entered into and (iii) does not otherwise violate any of the terms
            hereof.
          </Text>
        </Text>
        <Text>
          <b>(c)</b> <b>No Implied Rights.</b>
          <Text as="span">
            {' '}
            All rights not expressly granted herein are reserved by HOVER.
            Neither party grants the other party any interest or title in or
            rights to use its trademarks, service marks, or other proprietary
            symbols or designations (“Marks”), provided that during the Term,
            HOVER is hereby granted a limited, non-exclusive, non-transferable
            license to (i) use Sponsor’s Marks solely in connection with
            providing the Software and Services to Sponsor, and (ii) for
            Co-branded Apps, to reproduce and use Sponsor Marks listed on the
            Order Documentation within the Co-branded App, provided HOVER
            observes the usage guidelines described in the Order Documentation
            as may be amended by Sponsor. It is further provided that HOVER
            grants to Sponsor a limited, non-exclusive, non-transferable license
            during the Term to reproduce and use HOVER Marks listed on the Order
            Documentation within the Co-branded App or a HOVER Report, provided
            Sponsor observes the usage guidelines described in the Order
            Documentation as may be amended by HOVER. All goodwill associated
            with the use of either party’s Marks in connection with the
            Co-branded App inures solely to HOVER for its HOVER Marks and to
            Sponsor for its Sponsor Marks, and neither party will take action to
            damage the goodwill associated with the other’s Marks. In the event
            Sponsor notifies HOVER in writing that the Co-branded App does not
            conform to the requirements of the Order Documentation, HOVER will
            discontinue display of Sponsor Marks in the Co-branded App or make
            revisions satisfactory to Sponsor; and in the event HOVER notifies
            Sponsor in writing that the Co-branded App or a HOVER Report does
            not conform to the requirements of the Order Documentation, Sponsor
            will discontinue display of HOVER Marks in the Co-branded App or
            HOVER Report or make revisions satisfactory to HOVER.
          </Text>
        </Text>
        <TermsOfUseHeader>5. No Unlawful or Prohibited Use</TermsOfUseHeader>
        <Text>
          <Text as="span">
            The license to the Software and Services is conditioned on the
            following:
          </Text>
        </Text>
        <Text>
          <b>(a)</b>
          <Text as="span">
            {' '}
            the use will not have any purpose that is unlawful or prohibited by
            this Agreement;
          </Text>
        </Text>
        <Text>
          <b>(b)</b>
          <Text as="span">
            {' '}
            the use will not, in any manner, damage, disable, overburden, or
            impair the Software and Services or interfere with any other party’s
            use and enjoyment of the Software and Services;
          </Text>
        </Text>
        <Text>
          <b>(c)</b>
          <Text as="span">
            {' '}
            there will be no attempt to obtain any materials or information not
            provided for in the HOVER Deliverables; and
          </Text>
        </Text>
        <Text>
          <b>(d)</b>
          <Text as="span">
            {' '}
            the HOVER App will not be downloaded or used in, or transported to,
            a country that is subject to a U.S. Government embargo or sanction.
          </Text>
        </Text>
        <TermsOfUseHeader>
          6. Rights and Responsibilities for Images and Accounts
        </TermsOfUseHeader>
        <Text>
          <b>(a)</b>
          <Text as="span">
            {' '}
            By submitting Image Data and Property Information through the HOVER
            App, you represent and warrant to HOVER that you own or have all
            necessary rights, permissions, and licenses to submit and provide
            the Image Data and Property Information, and grant to HOVER the
            license and rights granted herein;
          </Text>
        </Text>
        <Text>
          <b>(b)</b>
          <Text as="span">
            {' '}
            You hereby grant to HOVER and its affiliates, licensees, agents, and
            sub-licensees, without compensation to you or others, a perpetual,
            irrevocable, non-exclusive, royalty-free, transferable, assignable,
            sub-licensable, worldwide license, to copy, adapt, create derivative
            works of, reproduce, incorporate, distribute, publicly display or
            otherwise use or exploit the Image Data throughout the world in any
            format or media (whether now known or hereafter created) for the
            duration of any copyright or other rights in such Image Data.
            Further, to the extent permitted under applicable law, you waive and
            release and covenant not to assert any moral rights that you may
            have in any Image Data submitted by you;
          </Text>
        </Text>
        <Text>
          <b>(c)</b>
          <Text as="span">
            {' '}
            You may provide suggestions, comments or other feedback
            (collectively,{' '}
          </Text>
          <b>“Feedback”</b>
          <Text as="span">
            ) to HOVER with respect to its products and services, including the
            Software and Services. Feedback is entirely voluntary. In the event
            you furnish any Feedback to HOVER, HOVER may use Feedback for any
            purpose without obligation of any kind. To the extent a license is
            required under your intellectual property rights to make use of the
            Feedback, you hereby grant HOVER an irrevocable, non-exclusive,
            perpetual, royalty-free license to use the Feedback in connection
            with HOVER’s business, including the enhancement of Software and
            Services; and
          </Text>
        </Text>
        <Text>
          <b>(d)</b>
          <Text as="span">
            {' '}
            You agree to indemnify, defend and hold HOVER, any Sponsor, any
            Sponsor affiliate and any Service Provider (including our and their
            assignees, subsidiaries, affiliated companies, and the respective
            officers, directors, employees, shareholders, agents and
            representatives of any thereof) free and harmless from and against
            any liability, loss, injury, damage, demand, action, cost, expense,
            or claim of any kind or character, arising out of or in connection
            with any breach of this Agreement and use or possession by you of
            the Software and Services.
          </Text>
        </Text>
        <TermsOfUseHeader>
          7. HOVER Rights and Responsibilities
        </TermsOfUseHeader>
        <Text>
          <Text as="span">
            HOVER shall provide the following in accordance with the Software
            and Services, subject to the ordering of such HOVER Deliverables and
            payment of fees:
          </Text>
        </Text>
        <Text>
          <b>(a)</b>
          <Text as="span"> </Text>
          <b>HOVER Deliverables. </b>
          <Text as="span">
            Upon successful upload of the Image Data, HOVER will use the HOVER
            Production Process to build a HOVER Model and/or the other HOVER
            Deliverables as ordered.
          </Text>
        </Text>
        <Text>
          <b>(b)</b>
          <Text as="span"> </Text>
          <b>Estimation Platform.</b>
          <Text as="span">
            {' '}
            If set forth on the Order Documentation and made available to
            Sponsor and its Sponsored Users, an Estimate Deliverable shall be
            made available to enable quoting and ordering materials for
            Sponsored Users to generate work orders of a Subject Property based
            on a HOVER Model. IN THE EVENT THE HOVER APP OR ESTIMATION PLATFORM
            CAUSES SPONSOR OR ITS USERS TO ORDER AN INCORRECT AMOUNT OF
            MATERIALS, SPONSOR’S SOLE REMEDY WILL BE ACTUAL, DIRECT DAMAGES (NO
            CONSEQUENTIAL DAMAGES) EQUAL TO AN AMOUNT NOT TO EXCEED THE PRICE
            PAID FOR THE ESTIMATION DELIVERABLE.
          </Text>
        </Text>
        <Text>
          <b>(c)</b>
          <Text as="span"> </Text>
          <b>Technical Support.</b>
          <Text as="span">
            {' '}
            HOVER shall provide technical support by telephone during its normal
            business hours (9:00 am to 5:00 pm EST) and provide necessary
            maintenance and update services to ensure that the Software and
            Services perform as intended. Support services may also be accessed
            by email at <SupportLink /> or through the chat feature in the HOVER
            App.
          </Text>
        </Text>
        <Text>
          <b>(d)</b>
          <Text as="span"> </Text>
          <b>Compliance with Laws.</b>
          <Text as="span">
            {' '}
            HOVER shall ensure that all information related to Users provided
            hereunder are used, maintained and accessed by HOVER in compliance
            with applicable laws and regulations with respect to privacy and
            data security, including without limitation, Data Privacy Laws, the
            Telemarketing Sales Rule (as amended), the Telephone Consumer
            Protection Act of 1991 and the CAN­SPAM Act of 2003. Further, HOVER
            warrants and represents that it or its service providers maintain
            physical, administrative and electronic safeguards to prevent the
            unauthorized use, access, disclosure or modification of information
            that can be used, alone or in conjunction with other generally
            available information, to identify any specific natural person, and
            that such safeguards are in compliance with applicable laws and
            regulations.
          </Text>
        </Text>
        <TermsOfUseHeader>8. Sponsor Responsibilities</TermsOfUseHeader>
        <Text>
          <Text as="span">
            Sponsor shall ensure that all information related to Consumer Users
            provided hereunder are used, maintained and accessed by Sponsor in
            compliance with applicable Data Privacy Laws, including without
            limitation, the Telemarketing Sales Rule (as amended), the Telephone
            Consumer Protection Act of 1991 and the CAN­SPAM Act of 2003.
          </Text>
        </Text>
        <TermsOfUseHeader>9. Customizations</TermsOfUseHeader>
        <Text>
          <b>(a)</b>
          <Text as="span"> </Text>
          <Text as="span">
            If so indicated on the Order Documentation, HOVER will perform the
            specified Customization Services for a Sponsor, if any (the
            “Specifications”). Upon completion of the Customization Services
            (the “Customization Deliverables”), HOVER shall notify Sponsor of
            such completion (“Notice”). Sponsor shall have five (5) business
            days (the “Acceptance Period”) from receipt of the Notice to
            determine whether the completed Customization Deliverables
            substantially conform to the Specifications. Upon expiration of the
            Acceptance Period, the completed Customization Deliverables shall be
            deemed accepted by Sponsor unless HOVER has received from Sponsor
            prior to the expiration of the Acceptance Period, a written
            statement detailing any substantial nonconformance (“Notice of
            Nonconformance”). If HOVER receives a Notice of Nonconformance
            during the Acceptance Period, HOVER will use reasonable commercial
            efforts to correct and redeliver the applicable Customization
            Deliverables to Sponsor. In the event the applicable Customization
            Deliverable was originally performed on a time and materials basis,
            Sponsor will pay for the correction work on the same time and
            materials. The Acceptance Period and procedure of this Section shall
            repeat itself with respect to such redelivered Customization
            Deliverables until accepted or deemed accepted by Sponsor.
          </Text>
        </Text>
        <Text>
          <b>(b)</b>
          <Text as="span"> </Text>
          <Text as="span">
            If at any time after acceptance of a Customization Deliverables
            Sponsor desires to modify or enhance the Customization Deliverables,
            or any portion thereof, Sponsor shall notify HOVER with respect to
            all such requested modifications, supplements or enhancements and
            provide to HOVER such proposed specifications as then exist. HOVER
            will thereupon notify Sponsor of the charges to be incurred with
            respect to such proposed modifications, supplements or enhancements,
            and time estimated for completion thereof.
          </Text>
        </Text>
        <TermsOfUseHeader>10. Fees</TermsOfUseHeader>
        <Text>
          <b>(a) General.</b>
          <Text as="span">
            {' '}
            The fees and other charges, if any, for the Software and Services
            and HOVER Deliverables provided thereunder may be subject to
            additional taxes and duties (such as sales, use, value-added, or
            excise); HOVER may be required to collect or pay now or at any time
            in the future additional amounts with respect to such fees owed and
            User or applicable Sponsor shall pay any such tax (excluding taxes
            on HOVER’s net income). Specific payment structure may be set out in
            an Order Documentation; fees are payable by the User or party as set
            forth on the Order Documentation. All pricing is in U.S. Dollars.
            Subscription fees, if applicable, are subject to proration and are
            non-refundable.
          </Text>
        </Text>
        <Text>
          <b>(b) Billing.</b>
          <Text as="span">
            {' '}
            Payment of the amounts due to HOVER shall be made in accordance with
            the payment method and credit terms as selected by User or as set
            forth on an associated Order Documentation. If you have provided a
            credit card to HOVER, you authorize HOVER to charge your credit card
            with the amount of any purchase of Software or Services or HOVER
            Deliverables. Any amount due but not paid within thirty (30) days
            shall bear a finance charge at the rate of 1.5% per month.
          </Text>
        </Text>
        <Text>
          <b>(c) Reliance on charges.</b>
          <Text as="span">
            {' '}
            Sponsor is responsible for paying applicable fees for all HOVER
            Deliverables ordered by, distributed to or purchased by a User
            through Sponsor’s account. Sponsor is solely responsible for, and
            HOVER will bear no responsibility for, unauthorized access to or use
            of the Software and Services by any person who uses Sponsor
            credentials. HOVER shall be entitled to rely on User’s username,
            password and any Sponsor identifier code and/or claim number
            supplied by any User to determine whether such user is an authorized
            User on Sponsor’s account.
          </Text>
        </Text>
        <TermsOfUseHeader>
          11. Production and Delivery of HOVER Deliverables
        </TermsOfUseHeader>
        <Text>
          <Text as="span">
            HOVER does not guarantee the production and delivery of HOVER
            Deliverables. If HOVER is unable to produce a HOVER Deliverable due
            to failure to submit the required Image Data or other required
            information, or for any other reason, you will receive an email
            stating that your order cannot be processed and no charges will
            accrue for your order. Once a HOVER Deliverable is successfully
            produced, you will be charged for the order at the time the HOVER
            Deliverable is delivered to you.
          </Text>
        </Text>
        <Text>
          <Text as="span">
            A link to any HOVER Deliverables ordered by you shall be emailed to
            you upon completion. HOVER Reports shall be delivered through the
            HOVER App, by email in PDF format, or by other means as specified in
            an Order Documentation.
          </Text>
        </Text>
        <TermsOfUseHeader>
          12. Warranties and Limitation of Liability
        </TermsOfUseHeader>
        <Text>
          <b>(a)</b>
          <Text as="span"> </Text>
          <b>Sponsor Indemnification.</b>
          <Text as="span">
            {' '}
            Sponsor hereby agrees to defend and indemnify HOVER against, and
            hold HOVER harmless from, any loss, claim, cost, judgment, liability
            or expense (collectively, “Claims“), including court costs and
            reasonable fees of attorneys and other professionals, arising out of
            or in connection with any third party Claims arising from (i) a
            breach of this Agreement or any of the covenants contained herein by
            Sponsor, or (ii) the use of any Sponsor products and services.
          </Text>
        </Text>
        <Text>
          <b>(b)</b>
          <Text as="span"> </Text>
          <b>HOVER Indemnification</b>
        </Text>
        <Text>
          <b>(i)</b>
          <Text as="span"> </Text>
          <b>In General.</b>
          <Text as="span">
            {' '}
            HOVER will indemnify, defend and hold harmless Sponsor, its
            affiliates and their respective directors, officers, employees and
            agents from and against any and all third party Claims, including
            court costs and reasonable attorneys’ and other professionals’ fees,
            arising out of or in connection with (i) HOVER’s negligence, willful
            misconduct or other tortious action, (ii) a claim that the Services
            and Software or Sponsor’s use thereof infringes or violates any
            patent, copyright, trademark, or other intellectual property right
            of a third party or misappropriates any trade secret, or (iii) any
            personal injury (including death) or damage to property resulting
            from HOVER’s acts or omissions, provided that Sponsor promptly
            notifies HOVER of any such claim in writing and HOVER is given sole
            control over the defense and settlement of such claim. HOVER may,
            without the knowledge or consent of Sponsor, agree to any resolution
            of the dispute that does not require on the part of Sponsor a
            payment or an admission of wrongdoing. Without limiting the
            preceding sentence, HOVER may, in its sole discretion and in the
            event of a third party infringement claim, (A) seek to obtain
            through negotiation the right of Sponsor to continue using the
            Software and Services; (B) rework the Software and Services so as to
            make them non-infringing; or (C) replace the Software and Services,
            as long as the reworked or replacement Software and Services does
            not result in a material adverse change in the operational
            characteristics of the Software and Services. If none of these
            alternatives is reasonably available in HOVER’s sole discretion,
            HOVER may terminate this Agreement and the license granted herein,
            and Sponsor shall have no further obligation to HOVER.
          </Text>
        </Text>
        <Text>
          <b>(ii)</b>
          <Text as="span"> </Text>
          <b>Exceptions.</b>
          <Text as="span">
            {' '}
            The foregoing warranty and indemnity shall not apply to infringement
            caused by (A) Sponsor’s unauthorized modification or use of the
            Software and Services, including, without limitation, HOVER
            Deliverables; (B) Sponsor’s failure to use corrections or
            enhancements made available by HOVER, after actual notice of the
            corrections or enhancements is provided to Sponsor, and to the
            extent that such corrections or enhancements would make the Software
            and Services non-infringing; or (C) use of the Software and Services
            in excess of the rights granted in this Agreement.
          </Text>
        </Text>
        <Text>
          <b>(c)</b>
          <Text as="span"> </Text>
          <b>No Other Warranties; Limitation of Liability.</b>
          <Text as="span">
            {' '}
            THE SOFTWARE AND SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE”
            AND SPONSOR AND ITS USERS AGREE TO USE THEM AT THEIR OWN RISK. HOVER
            MAKES NO OTHER GUARANTEES, REPRESENTATIONS OR WARRANTIES OF ANY
            KIND, EXPRESS OR IMPLIED, ARISING BY LAW OR OTHERWISE, INCLUDING BUT
            NOT LIMITED TO, CONTENT, QUALITY, ACCURACY, COMPLETENESS,
            EFFECTIVENESS, RELIABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            USEFULNESS, USE OR RESULTS TO BE OBTAINED FROM THE SOFTWARE AND
            SERVICES EXCEPT THOSE SET FORTH HEREIN. NO ORAL OR WRITTEN
            INFORMATION OR ADVICE GIVEN BY HOVER OR ITS AUTHORIZED
            REPRESENTATIVES WILL CREATE ANY OTHER WARRANTIES OR IN ANY WAY
            INCREASE THE SCOPE OF HOVER’S OBLIGATIONS HEREUNDER. HOVER
            (INCLUDING ITS LICENSEES, ASSIGNEES, SUBSIDIARIES, AFFILIATES, AND
            THE RESPECTIVE OFFICERS, DIRECTORS, EMPLOYEES, SHAREHOLDERS AND
            REPRESENTATIVES) SHALL NOT BE LIABLE FOR ANY LOSS OF PROFIT,
            REVENUE, CONTRACTS OR SAVINGS, OR ANY OTHER INDIRECT, INCIDENTAL,
            SPECIAL OR CONSEQUENTIAL DAMAGES ARISING OUT OF THIS AGREEMENT OR
            SPONSOR’S OR ANY USER’S USE OF OR INABILITY TO USE THE SOFTWARE AND
            SERVICES, ANY DEFECT OR ERROR IN ANY SOFTWARE AND SERVICES, OR ANY
            BREACH OF THIS AGREEMENT, WHETHER IN AN ACTION IN CONTRACT OR TORT
            OR BASED ON A WARRANTY. SUBJECT TO ITS INDEMNIFICATION OBLIGATIONS
            IN SECTION 7(C), THE AGGREGATE LIABILITY OF HOVER (INCLUDING ITS
            LICENSEES, ASSIGNEES, SUBSIDIARIES, AFFILIATES, AND THE RESPECTIVE
            OFFICERS, DIRECTORS, EMPLOYEES, SHAREHOLDERS AND REPRESENTATIVES)
            FOR ALL CLAIMS AND DAMAGES OF EVERY KIND AND TYPE, WHETHER IN
            CONTRACT OR TORT ARISING OUT OF OR IN CONNECTION WITH THIS
            AGREEMENT, INCLUDING RESULTING FROM THE USE OR POSSESSION OF THE
            SOFTWARE AND SERVICES, SHALL NOT EXCEED, IN THE AGGREGATE, THE FEES
            PAID HEREUNDER BY SPONSOR IN THE THREE MONTHS IMMEDIATELY PRECEDING
            THE FIRST EVENT GIVING RISE TO LIABILITY. IN THE EVENT OF A MATERIAL
            ERROR IN ANY HOVER MODEL OR OTHER HOVER DELIVERABLE, SPONSOR’S SOLE
            REMEDY WILL BE A REFUND OF THE FEES PAID TO OBTAIN THE HOVER MODEL
            OR OTHER HOVER DELIVERABLE.
          </Text>
        </Text>
        <TermsOfUseHeader>13. Term</TermsOfUseHeader>
        <Text>
          <b>(a)</b>
          <Text as="span"> </Text>
          <b>Duration.</b>
          <Text as="span">
            {' '}
            Except as otherwise set forth on Order Documentation, the initial
            term of this Agreement shall be one (1) year from the date this
            Agreement is entered into, unless earlier terminated by any party in
            accordance with the terms hereof (the “Term”). Either party may
            terminate this Agreement (i) immediately if the other party becomes
            the subject of a petition in bankruptcy or any other proceeding
            relating to insolvency, receivership, liquidation or assignment for
            the benefit of creditors or (ii) upon thirty (30) days written
            notice to the other party if a material breach occurs and if such
            breach remains uncured at the expiration of such period. Any
            subscription fee or jobs commitment fee set forth on the Order
            Documentation shall be applicable for the term set forth in the
            Order Documentation. If this Agreement is terminated by HOVER in
            accordance with this Section, Sponsor will pay any unpaid fees
            incurred prior to the date of termination under this Agreement. In
            no event will termination relieve Sponsor of its obligation to pay
            any commitments or other fees payable to HOVER. The sections of this
            Agreement which by their nature are intended to survive shall
            survive any termination or expiration of this Agreement, including
            without limitation the provisions relating to license and use
            restrictions, intellectual property rights, limitations of liability
            and confidentiality, which shall specifically survive termination of
            this Agreement.
          </Text>
        </Text>
        <Text>
          <b>(b)</b>
          <Text as="span"> </Text>
          <b>Effect of Termination.</b>
          <Text as="span">
            {' '}
            Upon any termination of this Agreement, except as otherwise provided
            in Section 4(b), the license to the Software and Services shall
            terminate.
          </Text>
        </Text>
        <TermsOfUseHeader>14. Intellectual Property</TermsOfUseHeader>
        <Text>
          <Text as="span">
            The Software and Services, including all HOVER Models and other
            HOVER Deliverables, are the property of HOVER, and subject to the
            copyright and other intellectual property rights of HOVER and its
            licensors. Use of the Software and Services or HOVER Production
            Process does not create a work-for-hire relationship agreement (as
            that term is defined in 17 U.S.C. § 101).
          </Text>
        </Text>
        <Text>
          <Text as="span">
            Any technology developed in connection with providing the Software
            and Services or any Customization Services, or improvements or
            modifications to the foregoing, shall be owned by HOVER; provided
            however, that HOVER does not have the right to incorporate any
            Confidential Information into the Software and Services or any other
            product or service.
          </Text>
        </Text>
        <Text>
          <Text as="span">
            Subject to the foregoing, HOVER shall have the exclusive right to
            apply for or register any patents, mask work rights, copyrights, and
            such other proprietary rights protections with respect thereto, and
            you hereby grant all right, title and interest you may have in the
            foregoing to HOVER.
          </Text>
        </Text>
        <Text>
          <Text as="span">
            HOVER will review allegations of copyright violations in accordance
            with the Digital Millennium Copyright Act (“DMCA”); notices of
            allegedly infringing Image Data or other content (collectively,
            “Content”) shall be sent to <SupportLink /> or:
          </Text>
        </Text>
        <Text>
          <Text as="span">HOVER – Copyright Notices</Text>
        </Text>
        <Text>
          <Text as="span">{ADDRESS_LINE_1}</Text>
        </Text>
        <Text>
          <Text as="span">{ADDRESS_LINE_2}</Text>
        </Text>
        <TermsOfUseHeader>15. Confidentiality and Security</TermsOfUseHeader>
        <Text>
          <b>(a)</b>
          <Text as="span"> </Text>
          <b>Confidentiality.</b>
          <Text as="span">
            {' '}
            Any and all confidential documentation and information provided by
            one party to the other party under this Agreement (“Confidential
            Information”) is confidential and proprietary to the disclosing
            party, and shall remain the property of the disclosing party. The
            receiving party agrees that it will not permit the duplication, use
            or disclosure of any such Confidential Information to any person
            (other than its own employees, agents or representatives who must
            have such information for the performance of its obligations
            hereunder), unless such duplication, use or disclosure is
            specifically authorized in writing in advance by the disclosing
            party. Confidential Information of the disclosing party may include,
            without limitation, the disclosing party’s past, present and future
            research, development and business activities, inventions, software
            code, database lists, technical information, plans, methods,
            know-how, processes and techniques regarding manufacturing and sale,
            and disclosing party’s customers, creditors and suppliers, whether
            in oral, written, graphic or electronic form. All Confidential
            Information will remain the exclusive property of disclosing party
            and nothing contained in this Agreement shall be construed as
            granting to the receiving party any rights therein, by license or
            otherwise, except the limited right to use such Confidential
            Information solely in the performance of this Agreement. The
            receiving party agrees to destroy or return upon request of the
            disclosing party all Confidential Information which is in written,
            graphic or other tangible form furnished to the receiving party or
            acquired by the receiving party under this Agreement. Confidential
            Information shall not include any information which at the time of
            disclosure: (i) was previously known to the receiving party without
            obligations of confidentiality; (ii) is obtained after the date
            hereof from a third party which is lawfully in possession of such
            information and not in violation of any contractual or legal
            obligation to a disclosing party with respect to such information;
            (iii) is or becomes part of the public domain or otherwise
            accessible by the public through no fault of receiving party or its
            employees; (iv) is independently ascertainable or developed by
            receiving party or its employees; (v) is required to be disclosed by
            administrative or judicial action provided that receiving party
            immediately after receiving notice of such action notifies
            disclosing party of such action to give the disclosing party an
            opportunity to seek any other legal remedies to maintain such
            Confidential Information in confidence; or (vi) is approved for
            release by written authorization of disclosing party. For the
            avoidance of doubt, the Derived Data shall not be deemed to be the
            Confidential Information of Sponsor.
          </Text>
        </Text>
        <Text>
          <b>(b)</b>
          <Text as="span"> </Text>
          <b>Security of Derived Data and Photos.</b>
          <Text as="span">
            {' '}
            HOVER covenants that except to perform its obligations under this
            Agreement, all Derived Data will only be used in an anonymized
            format and any Image Data will only be used for internal purposes.
          </Text>
        </Text>
        <TermsOfUseHeader>16. Relationship of Parties</TermsOfUseHeader>
        <Text>
          <Text as="span">
            Each party will be and act as an independent contractor and not as
            an agent or partner of, joint-venturer with the other party for any
            purpose related to this Agreement or the transactions contemplated
            hereunder, and neither party will by virtue of this Agreement have
            any right, power or authority to act or create any obligation,
            expressed or implied, on behalf of the other party.
          </Text>
        </Text>
        <TermsOfUseHeader>17. Assignment</TermsOfUseHeader>
        <Text>
          <Text as="span">
            The rights and obligations described herein and as may be further
            described in any Order Documentation may not be assigned in whole or
            in part by either party without the prior written consent of the
            other party, which consent will not be unreasonably withheld or
            delayed, except that (a) a Sponsor may assign its rights to any
            subsidiary or affiliate without prior written consent, but with
            notice to HOVER and (b) HOVER may assign in connection with a sale
            of all, or substantially all, of the assets, business or equity of
            HOVER so long as the sale or transfer of assets is not to a
            competitor of an applicable Sponsor.
          </Text>
        </Text>
        <TermsOfUseHeader>18. Notices</TermsOfUseHeader>
        <Text>
          <Text as="span">
            All notices concerning, required or permitted under this Agreement
            will be sent to{' '}
            <a
              href="/cdn-cgi/l/email-protection"
              className="__cf_email__"
              data-cfemail="186b6d6868776a6c5870776e7d6a366c77"
            >
              [email&#160;protected]
            </a>{' '}
            unless otherwise designated in Order Documentation.
          </Text>
        </Text>
        <TermsOfUseHeader>19. Miscellaneous</TermsOfUseHeader>
        <Text>
          <b>(a) </b>
          <Text as="span">
            Neither party will incur any liability to the other on account of
            any loss or damage resulting from any delay or failure to perform
            all or any part of this Agreement if such delay or failure is
            caused, in whole or in part, by events, occurrences, or causes
            beyond its control and without negligence of the parties. Such
            events, occurrences or causes will include, without limitation, acts
            of God, strikes, lockouts, riots, acts of war, terrorism,
            earthquakes, fire and explosions, but the ability to meet financial
            obligations is expressly excluded.
          </Text>
        </Text>
        <Text>
          <b>(b) </b>
          <Text as="span">
            Any waiver of the provisions of this Agreement or of a party’s
            rights or remedies under this Agreement must be in writing to be
            effective. Failure, neglect or delay by a party to enforce the
            provisions of this Agreement or its rights or remedies at any time
            will not be construed to be deemed a waiver of such party’s rights
            and will not in any way affect the validity of the whole or any part
            of this Agreement or prejudice such party’s right to take subsequent
            action.
          </Text>
        </Text>
        <Text>
          <b>(c)</b>
          <Text as="span">
            {' '}
            If any term, condition or provision in this Agreement is found to be
            invalid, unlawful or unenforceable to any extent, the parties shall
            endeavor in good faith to agree to such amendments that will
            preserve, as far as possible, the intentions expressed in this
            Agreement. If the parties fail to agree on such an amendment, such
            invalid term, condition or provision will be severed from the
            remaining terms, conditions and provisions, which will continue to
            be valid and enforceable to the fullest extent permitted by law.
          </Text>
        </Text>
        <Text>
          <b>(d)</b>
          <Text as="span">
            {' '}
            This Agreement contains the entire agreement of the parties with
            respect to the subject matter herein and supersedes all previous
            communications, proposals representations, understandings and
            agreements, either oral or written, between the parties with respect
            to said subject matter. No employee, agent, representative or
            affiliate of HOVER or Sponsor has authority to bind HOVER or
            Sponsor, respectively, to any oral representations or warranty
            concerning any HOVER products or services or additional Sponsor
            obligations. Any written representation or warranty not expressly
            contained in this Agreement is unenforceable.
          </Text>
        </Text>
        <Text>
          <b>(e) </b>
          <Text as="span">
            No terms, provisions or conditions of any purchase order,
            acknowledgement or other business form that Sponsor may use in
            connection with the acquisition or licensing of the Software and
            Services will have any effect on the rights, duties or obligations
            of the parties under, or otherwise modify, this Agreement regardless
            of any failure of HOVER to object to such terms, provisions, or
            conditions, except where the same have been mutually agreed-to in
            writing by the parties.
          </Text>
        </Text>
        <Text>
          <b>(f)</b>
          <Text as="span">
            {' '}
            This Agreement may be executed in counterparts, including by .pdf
            format, each of which so executed will be deemed to be an original
            and such counterparts together will constitute one and the same
            instrument.
          </Text>
        </Text>
        <Text>
          <b>(g)</b>
          <Text as="span">
            {' '}
            This Agreement will be interpreted and construed pursuant to the
            laws of the State of New York and the United States without regard
            to conflict of laws provisions thereof, and without regard to the
            United Nations Convention on the International Sale of Goods. In the
            event of a dispute arising under this Agreement, the prevailing
            party shall be entitled to recover reasonable attorneys’ fees and
            costs, provided that if a party prevails only in part the court
            shall award fees and costs in accordance with the relative success
            of each party.
          </Text>
        </Text>
        <Text>
          <b>(h)</b>
          <Text as="span">
            {' '}
            The parties agree to waive any future challenge to the validity and
            enforceability of any document submitted hereunder on the grounds
            that it was electronically transmitted and/or authorized.
          </Text>
        </Text>
        <Text>
          <b>(i)</b>
          <Text as="span">
            {' '}
            The language of this Agreement shall be construed in accordance with
            its fair meaning and not for or against any party. The parties
            acknowledge that each party has had the opportunity to review this
            Agreement with counsel, and accordingly the rule of construction
            that would resolve ambiguities in favor of non-drafting parties
            shall not apply to the interpretation of this Agreement.
          </Text>
        </Text>
        <Text>
          <b>(j)</b>
          <Text as="span">
            {' '}
            All section and paragraph titles and captions contained in this
            Agreement are for convenience only and are not deemed a part of the
            context hereof.
          </Text>
        </Text>
      </Stack>
    </ContentWithStickySidebar>
  </Layout>
);

export default TermsOfUse;
